import { formatDistance } from 'date-fns'
import type { CoachingTransportDto } from '@/api/_trpc/routers/coaching-service-error'
import type { SessionCloseReason } from '@/db/schema/enums'
import type {
  ProblemAttemptDto,
  ProblemHistoryDto,
} from '@/lib/coaching/problem-history-query'

/**
 * Formats what a client has done with a problem before (LG-360 §5).
 *
 * `now` is injected rather than read from `Date.now()` so "2 days ago" is
 * testable without freezing the clock — the caller (the section component)
 * supplies the real clock; tests supply a fixed one.
 *
 * `formatDistance(at, now, { addSuffix: true })` rather than
 * `formatDistanceToNow` — the latter always measures against the real
 * `Date.now()`, which is exactly the untestable clock read this file exists
 * to avoid.
 */
export function summarizeHistory(
  history: ProblemHistoryDto,
  now: Date,
): { headline: string; detail: string | null } {
  // `coachReportedAt` is not read directly: `lastSeenAt` is the server's
  // `laterOf(platformLastAt, coachReportedAt)`, so it already reflects a
  // coach-only date when there is nothing from the platform to compare it to.
  const { platformAttemptCount, coachReported, lastSeenAt } = history

  if (platformAttemptCount === 0 && !coachReported) {
    return { headline: 'New to this client', detail: null }
  }

  // The most recent of the two sources, formatted once for both headline
  // shapes below. `lastSeenAt` can be null even when `coachReported` is true
  // — the coach said "yes" with no date — and that is reported honestly
  // rather than inventing one.
  const detail =
    lastSeenAt === null
      ? 'No date recorded'
      : `Most recently ${formatDistance(lastSeenAt, now, { addSuffix: true })}`

  if (platformAttemptCount > 0 && coachReported) {
    return {
      headline: `Done ${platformAttemptCount} times here, and before that per the coach`,
      detail,
    }
  }

  if (platformAttemptCount > 0) {
    return {
      // "1 times" reads as broken, so the singular gets its own word.
      headline:
        platformAttemptCount === 1
          ? 'Done once'
          : `Done ${platformAttemptCount} times`,
      detail,
    }
  }

  // Platform never saw it — every platform-attempt claim above is credited
  // to the coach instead, or the panel would say the platform saw something
  // it never did.
  return { headline: 'Done before, reported by the coach', detail }
}

// Typed against the DB's actual `SessionCloseReason` enum (not `string`) and
// `Partial` rather than exhaustive: a close reason with no entry here already
// has a correct fallback below ("Attempted"), but indexing this with anything
// other than a real `SessionCloseReason` — a typo, or a value widened back to
// `string` somewhere upstream — is now a type error instead of a silent
// `undefined`.
const CLOSE_REASON_LABEL: Partial<Record<SessionCloseReason, string>> = {
  solved: 'Solved',
  expired: 'Ran out of time',
}

/**
 * One log row. An open platform session (`closeReason: null`) reads
 * "Attempted", never "Solved" or "Ran out of time" — it is neither of
 * those, and guessing would misreport an in-progress session as a finished
 * one. An attempt on a duplicate twin (LG-442) names the twin, so the coach
 * can tell "solved this" from "solved the same question under another name".
 */
export function describeAttempt(attempt: ProblemAttemptDto, now: Date): string {
  const when = formatDistance(attempt.at, now, { addSuffix: true })
  const label =
    attempt.provenance === 'coach'
      ? 'Coach-reported'
      : attempt.closeReason === null
        ? 'Attempted'
        : (CLOSE_REASON_LABEL[attempt.closeReason] ?? 'Attempted')
  const via = attempt.via === null ? '' : ` (via ${attempt.via.title})`
  return `${label} — ${when}${via}`
}

/**
 * Reconciles the wire shape back to the real DTOs the formatters above are
 * typed and tested against.
 *
 * `withCoachingServiceErrors` (`api/_trpc/routers/coaching-service-error.ts`)
 * serializes every `Date` to an ISO string for the trip over tRPC, so what
 * reaches the client is `CoachingTransportDto<ProblemHistoryDto>` /
 * `CoachingTransportDto<ProblemAttemptDto>` — `string`s where the server-side
 * DTO has `Date`s — not the DTOs themselves. This is the one place that
 * string→Date conversion happens for §5: `summarizeHistory` and
 * `describeAttempt` are typed and unit-tested against real `Date`s, and the
 * wire shape must never reach them directly, or a formatter that passes its
 * own `Date`-fixture tests would silently receive strings in production.
 */
export function parseProblemHistory(
  history: CoachingTransportDto<ProblemHistoryDto>,
): ProblemHistoryDto {
  return {
    ...history,
    coachReportedAt:
      history.coachReportedAt === null
        ? null
        : new Date(history.coachReportedAt),
    lastSeenAt:
      history.lastSeenAt === null ? null : new Date(history.lastSeenAt),
  }
}

export function parseProblemAttempts(
  attempts: readonly CoachingTransportDto<ProblemAttemptDto>[],
): ProblemAttemptDto[] {
  return attempts.map((attempt) => ({ ...attempt, at: new Date(attempt.at) }))
}
